import { useQuery, useMutation, useQueryClient } from 'react-query';

import {
  useRequest,
  type MutationFunction,
  type QueryFunction,
} from '../common';
import {
  getActionDefinitionDetailsKey,
  getActionDefinitionListKey,
  getManagedIntegrationDetailsKey,
  getManagedIntegrationKey,
} from './keys';

//Fetches single action definition per UUID
export const useActionDefinition: QueryFunction<
  unknown,
  [actionDefitionUuid: string],
  ReturnType<typeof getActionDefinitionDetailsKey>
> = (actionDefinitionUuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getActionDefinitionDetailsKey(actionDefinitionUuid),
    queryFn: ({ signal }) =>
      request(
        `/managed_integrations/integration_action_definitions/${actionDefinitionUuid}`,
        {
          init: {
            signal,
          },
          query,
        },
      ),
  });
};

//Fetches entire list of available action definitions
export const useActionDefinitionsList: QueryFunction<
  any[],
  [managedIntegrationUuid: string],
  ReturnType<typeof getActionDefinitionListKey>
> = (managedIntegrationUuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getActionDefinitionListKey(),
    queryFn: ({ signal }) =>
      request(
        `/managed_integrations/${managedIntegrationUuid}/integration_action_definitions`,
        {
          init: {
            signal,
          },
          query,
        },
      ),
  });
};

//This create an action definition, overwrites QueryData for that individual action,
//and invalidates all related action definition list queries
export const useCreateActionDefinition: MutationFunction<
  unknown,
  { uuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ uuid, body }) => {
      return request(
        `/managed_integrations/${uuid}/integration_action_definitions`,
        {
          query,
          init: { method: 'POST', body },
        },
      );
    },
    {
      ...queryOptions,
      mutationKey: ['createActionDefinition'],
      onSuccess: (data, requestData, ...rest) => {
        const key = getActionDefinitionDetailsKey(requestData.uuid);
        queryClient.setQueryData(key, data);
        queryClient.invalidateQueries(getActionDefinitionListKey());
        queryOptions?.onSuccess?.(data, requestData, ...rest);
        //This is necessary because we ~sometimes~ rely on the managedIntegrations API
        //to get a list of verbose actionDefinitions
        queryClient.invalidateQueries(
          getManagedIntegrationDetailsKey(requestData.uuid),
        );
      },
    },
  );
};

//This edits an action definition, overwrites QueryData for that individual action,
//and invalidates all related action definition list queries
export const useEditActionDefinition: MutationFunction<
  unknown,
  { uuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ uuid, body }) => {
      return request(
        `/managed_integrations/integration_action_definitions/${uuid}`,
        {
          query,
          init: { method: 'PATCH', body },
        },
      );
    },
    {
      ...queryOptions,
      mutationKey: ['editActionDefinition'],
      onSuccess: (data, requestData, ...rest) => {
        const key = getActionDefinitionDetailsKey(requestData.uuid);
        queryClient.setQueryData(key, data);
        queryClient.invalidateQueries(getActionDefinitionListKey());
        queryOptions?.onSuccess?.(data, requestData, ...rest);
        //This is necessary because we ~sometimes~ rely on the managedIntegrations API
        //to get a list of verbose actionDefinitions
        //(no UUID available - invalidate all managed integrations)
        queryClient.invalidateQueries(getManagedIntegrationKey());
      },
    },
  );
};

//This deletes an action definition, overwrites QueryData for that individual action,
//and invalidates all related action definition list queries
export const useDeleteActionDefinition: MutationFunction<
  unknown,
  { integrationActionDefinitionId: string }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ integrationActionDefinitionId }) => {
      return request(
        `/managed_integrations/integration_action_definitions/${integrationActionDefinitionId}`,
        {
          query,
          init: { method: 'DELETE' },
        },
      );
    },
    {
      ...queryOptions,
      mutationKey: ['deleteActionDefinition'],
      onSuccess: (data, requestData, ...rest) => {
        const key = getActionDefinitionDetailsKey(
          requestData.integrationActionDefinitionId,
        );
        queryClient.setQueryData(key, data); // Do we need this?
        queryClient.invalidateQueries(getActionDefinitionListKey());
        queryOptions?.onSuccess?.(data, requestData, ...rest);
        //This is necessary because we ~sometimes~ rely on the managedIntegrations API
        //to get a list of verbose actionDefinitions
        //(no UUID available - invalidate all managed integrations)
        queryClient.invalidateQueries(getManagedIntegrationKey());
      },
    },
  );
};

export default {
  useActionDefinitionsList,
  useActionDefinition,
  useCreateActionDefinition,
  useEditActionDefinition,
  useDeleteActionDefinition,
};
